/*
  Shared "drain" effect engine for every collection banner on the site
  (the Sets Collection whirlpool, generalized). One copy of this file is
  loaded by every banner section that can show it:
    - sections/new-image-banner.liquid       (photo banners)
    - sections/main-collection-banner.liquid (text-only banners)
    - sections/position-image-banner.liquid  (composite/multi-image banners)
  so the math, the star shapes, and the scroll-progress logic only exist
  once, instead of being copied per page. Each caller just wires its own
  DOM elements into window.StarSniffDrainBanner.initPhoto / initComposite
  / initText.

  Progress is always derived purely from scroll position
  (getBoundingClientRect), never from wheel/touch/key events, so it is
  identical across mouse wheel, trackpad, scrollbar drag and touch
  scrolling. There is no pinning: the page scroll is never blocked, the
  banner just leaves with the drain partly done if the visitor scrolls
  fast. Reaches progress 1 at 55% of the banner's own rendered height
  (not the full height), same reasoning as the original Sets build: at
  full height the drain would only resolve once the banner had already
  scrolled off-screen for most ordinary scroll speeds.
*/
(function () {
  'use strict';

  function smoothstep(t) { return t * t * (3 - 2 * t); }

  function hexToRgb(hex) {
    if (!hex) return null;
    var m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
    return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
  }

  function rgbToCss(rgb) {
    return 'rgb(' + rgb[0] + ',' + rgb[1] + ',' + rgb[2] + ')';
  }

  /*
    An override can be either one hex color (applies to every star, the
    original simple case) or several comma-separated hex colors (e.g.
    Sets' three hand-picked tones, restored exactly via the collection's
    own metafield — see the data-drain-color comment in
    sections/new-image-banner.liquid). Returns null, not an empty array,
    when nothing usable was found, so callers can tell "no override" apart
    from "override present but unparseable".
  */
  function parseColorList(str) {
    if (!str) return null;
    var parts = str.split(',');
    var colors = [];
    for (var i = 0; i < parts.length; i++) {
      var rgb = hexToRgb(parts[i]);
      if (rgb) colors.push(rgb);
    }
    return colors.length ? colors : null;
  }

  /*
    Assigns a list of colors across however many star paths exist,
    cycling if there are fewer colors than stars (e.g. a two-tone outfit
    on three stars repeats the dominant tone on two of them) and falling
    back to a single neutral mid-gray — deliberately NOT near-black —
    when colors is empty, so a detection failure never reads as "this is
    a black-stars text banner" on a page that actually has a photo.
  */
  function setStarColors(starPaths, colors) {
    var use = (colors && colors.length) ? colors : [[128, 128, 128]];
    for (var i = 0; i < starPaths.length; i++) {
      starPaths[i].style.fill = rgbToCss(use[i % use.length]);
    }
  }

  /*
    Flips .drain-banner__stars / .drain-banner__setstext from the
    hidden-by-default (opacity: 0) state set by the critical inline
    <style> in sections/new-image-banner.liquid /
    position-image-banner.liquid to visible, by adding this class to
    the shared .drain-banner__root element. Called once per banner,
    always as the very last step of that banner's own applyColor() —
    never before colors are actually set on the paths/letters, so
    there is no in-between frame where they're visible in some
    not-yet-colored (or worse, default black) state. From this point
    on the stars/word are exactly as opaque as they've always been;
    the existing z-index stacking (photo/canvas above, stars/word
    below) is what keeps them hidden until the user scrolls, same as
    before this fix — this only ever controls the one-time initial
    reveal, nothing about the scroll-driven drain itself.
  */
  function markDrainReady(mediaEl) {
    if (mediaEl) mediaEl.classList.add('is-drain-ready');
  }

  /*
    Skin detector using YCbCr chrominance (Cb 77-127, Cr 133-173, a
    standard published skin-detection range), not a direct RGB rule.
    An earlier RGB-based version required r > 95, which only caught
    lighter-to-medium skin and completely missed darker skin tones —
    confirmed on the Star Signs banner, where a model's deep brown
    forearm (around RGB 71,60,53) was never excluded and got picked as
    a "garment" color. That same earlier version also required r > g
    with a wide gap, which happens to also match saturated pink/magenta
    (e.g. a hot-pink garment around RGB 230,50,140 passed every one of
    its checks too) — the root cause of a pink-and-gray sweatshirt
    banner's stars coming out all gray, since the pink pixels were being
    thrown away as "skin" before the garment-color clustering ever saw
    them. Converting to Cb/Cr instead of reasoning in raw RGB fixes both
    at once: it's approximately luminance-independent, so it catches
    skin across light and dark tones alike, and pink's chrominance sits
    clearly outside the skin window. The real tradeoff, inherent to any
    color-only skin test (no face/body segmentation is available here):
    deep burgundy, tan, khaki and some browns are chromatically close
    enough to skin that they can also fall inside this window and get
    excluded — flagged in the verification report for any banner where
    that looked like it mattered.
  */
  function isSkinTone(r, g, b) {
    var cb = 128 - 0.168736 * r - 0.331264 * g + 0.5 * b;
    var cr = 128 + 0.5 * r - 0.418688 * g - 0.081312 * b;
    return cb >= 77 && cb <= 127 && cr >= 133 && cr <= 173;
  }

  function colorDistance(a, b) {
    var dr = a[0] - b[0], dg = a[1] - b[1], db = a[2] - b[2];
    return Math.sqrt(dr * dr + dg * dg + db * db);
  }

  /*
    WCAG relative luminance / contrast-against-white — kept only as a
    darkening TARGET for isLightGray colors below (see darkenGray),
    not as a broad filter. An earlier version of this file used this
    to drop ANY detected color under a 3:1 contrast floor, which threw
    away perfectly fine saturated colors too (anything moderately
    light), collapsing some outfits to a single near-black tone across
    every star and letter. That was reverted — detection is back to
    picking the plain largest-clusters-by-size result it always did;
    only literal white/near-white and literal light/washed-out gray
    get touched now (isNearWhite / isLightGray below), nothing else.
  */
  function relLuminance(rgb) {
    function chan(c) {
      var s = c / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    }
    return 0.2126 * chan(rgb[0]) + 0.7152 * chan(rgb[1]) + 0.0722 * chan(rgb[2]);
  }

  function contrastWithWhite(rgb) {
    return 1.05 / (relLuminance(rgb) + 0.05);
  }

  var MIN_CONTRAST = 3;

  /*
    chroma (max channel - min channel) is this file's simple stand-in
    for "how gray/neutral a color is" — low chroma means the channels
    are close together, i.e. gray regardless of how light or dark.
    isNearWhite and isLightGray both require low chroma; they differ
    only in how light the color is, so a saturated color (a real pink,
    red, navy, teal, etc.) never matches either one and is always left
    alone, no matter how light or dark it is — only literal whites and
    literal grays are in scope here, exactly what was asked for.
  */
  function chroma(rgb) {
    return Math.max(rgb[0], rgb[1], rgb[2]) - Math.min(rgb[0], rgb[1], rgb[2]);
  }

  function avgBrightness(rgb) {
    return (rgb[0] + rgb[1] + rgb[2]) / 3;
  }

  function isNearWhite(rgb) {
    return chroma(rgb) <= 15 && avgBrightness(rgb) >= 225;
  }

  function isLightGray(rgb) {
    return chroma(rgb) <= 20 && avgBrightness(rgb) >= 140 && avgBrightness(rgb) < 225;
  }

  /*
    Scales a light gray toward black, in place, until it clears
    MIN_CONTRAST — used only for isLightGray colors (see
    detectGarmentColors), never for a color with real hue/saturation.
    Repeated uniform RGB scaling keeps the channels in the same
    near-equal ratio they started in, so the result is still
    recognizably the same gray, just dark enough to read on white —
    "darker, same gray", not a different color.
  */
  function darkenGray(rgb) {
    var c = [rgb[0], rgb[1], rgb[2]];
    var guard = 0;
    while (contrastWithWhite(c) < MIN_CONTRAST && guard < 60) {
      c[0] *= 0.92; c[1] *= 0.92; c[2] *= 0.92;
      guard++;
    }
    return [Math.round(c[0]), Math.round(c[1]), Math.round(c[2])];
  }

  /*
    Estimates the banner's own background color by averaging a thin ring
    of pixels around the image's outer edge, rather than assuming
    "background = near-white/near-gray" the way an earlier version of
    this file did. That assumption was the actual bug behind stars
    turning black: a gray sweatshirt or a black dress IS the garment
    color, not background, but a blanket low-saturation/near-white
    exclusion threw those pixels out along with the real backdrop,
    frequently leaving too few qualifying pixels and tripping the
    near-black fallback that used to exist. Sampling the actual border —
    which in ordinary studio product photography is the plain backdrop,
    not the centered subject — lets genuinely neutral-colored clothing
    still register as its own foreground cluster.
  */
  function estimateBackground(data, w, h) {
    var sumR = 0, sumG = 0, sumB = 0, n = 0;
    var borderPx = Math.max(1, Math.round(Math.min(w, h) * 0.07));

    function addPixel(x, y) {
      var i = (y * w + x) * 4;
      if (data[i + 3] < 200) return;
      sumR += data[i]; sumG += data[i + 1]; sumB += data[i + 2]; n++;
    }

    for (var x = 0; x < w; x++) {
      for (var k = 0; k < borderPx; k++) { addPixel(x, k); addPixel(x, h - 1 - k); }
    }
    for (var y = 0; y < h; y++) {
      for (var k = 0; k < borderPx; k++) { addPixel(k, y); addPixel(w - 1 - k, y); }
    }
    return n ? [sumR / n, sumG / n, sumB / n] : null;
  }

  /*
    Picks up to maxColors dominant garment colors from a product photo,
    in descending order of how much of the garment they cover — so a
    two-tone outfit (e.g. a gray sweatshirt with a pink star print and
    pink pants) comes back as [gray, pink], not a single blended color
    or just whichever one happens to be more saturated.

    Method: downsample onto a small offscreen canvas, estimate and
    subtract the actual photographed background (see estimateBackground
    above, not a blanket brightness/saturation guess), exclude skin-tone
    pixels, then cluster what's left by color proximity (quantize into
    coarse buckets, greedily merge buckets that are close in RGB space)
    and return the largest clusters' average colors. Clusters covering
    under 3% of the qualifying pixels are dropped as noise unless
    nothing else survives. This selection is exactly what it was
    originally — a broader "drop anything under 3:1 contrast" pass was
    tried and reverted; it discarded too much (see isNearWhite/
    isLightGray below for what's actually still adjusted).

    Two narrow, targeted adjustments run on the chosen colors after
    selection, and nothing else:
    - A color that's white or near-white (isNearWhite) is swapped for
      the next-most-prominent color detected elsewhere in the same
      photo that isn't itself near-white — the outfit's actual "second
      color" (a print, trim, a second garment), not a darkened version
      of white.
    - A color that's a light/washed-out gray (isLightGray) is darkened
      in place (darkenGray) — same gray, just dark enough to read on
      white. Never swapped for a different color.
    Every other color — any real hue, any darkness, including an
    already-dark gray or an already-dark neutral — is returned exactly
    as detected, untouched.

    Returns [] — never null, never a near-black placeholder — if the
    image can't be read (e.g. a CORS-tainted canvas) or no foreground
    survives filtering, so the caller can fall back to a neutral gray
    instead of something that could be mistaken for an intentional
    black-stars text banner.
  */
  function detectGarmentColors(img, maxColors) {
    maxColors = maxColors || 3;
    try {
      var nw = img.naturalWidth, nh = img.naturalHeight;
      if (!nw || !nh) return [];
      /*
        300px wide, not the 120px this used to sample at: 120px on a
        typical ~1440px-wide banner photo means each sample pixel is an
        average of roughly 12x12 = 144 original pixels — a small accent
        color (a scattered star print, a thin trim) covering a modest
        patch of the real photo can get diluted into its surrounding
        fabric color by that averaging before a single cluster is even
        built, not merely outvoted by size afterward. Confirmed
        directly: a photo with a clearly visible blue star print sampled
        at 120px produced zero blue-ish clusters anywhere in the full
        cluster list, not just outside the top 3 — the blue pixels never
        survived resampling at all. 300px (about 4-5 original pixels per
        sample instead of 12) is still a tiny, fast canvas read, but
        preserves small real details well enough for the vivid-color
        logic below to actually find them.
      */
      var w = 300, h = Math.max(1, Math.round(300 * (nh / nw)));
      var c = document.createElement('canvas');
      c.width = w; c.height = h;
      var ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, w, h);
      var data = ctx.getImageData(0, 0, w, h).data;

      var bg = estimateBackground(data, w, h);
      var BG_THRESHOLD = 32;
      var STEP = 18;

      var buckets = {};
      for (var y = 0; y < h; y++) {
        for (var x = 0; x < w; x++) {
          var i = (y * w + x) * 4;
          var r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
          if (a < 200) continue;
          if (bg && colorDistance([r, g, b], bg) < BG_THRESHOLD) continue;
          if (isSkinTone(r, g, b)) continue;

          var key = Math.round(r / STEP) + '_' + Math.round(g / STEP) + '_' + Math.round(b / STEP);
          if (!buckets[key]) buckets[key] = { r: 0, g: 0, b: 0, n: 0 };
          buckets[key].r += r; buckets[key].g += g; buckets[key].b += b; buckets[key].n++;
        }
      }

      var bucketList = [];
      for (var k in buckets) bucketList.push(buckets[k]);
      bucketList.sort(function (p, q) { return q.n - p.n; });

      var MERGE_THRESHOLD = 40;
      var clusters = [];
      for (var bi = 0; bi < bucketList.length; bi++) {
        var bucket = bucketList[bi];
        var avg = [bucket.r / bucket.n, bucket.g / bucket.n, bucket.b / bucket.n];
        var merged = false;
        for (var ci = 0; ci < clusters.length; ci++) {
          if (colorDistance(avg, clusters[ci].avg) < MERGE_THRESHOLD) {
            var cl = clusters[ci];
            cl.r += bucket.r; cl.g += bucket.g; cl.b += bucket.b; cl.n += bucket.n;
            cl.avg = [cl.r / cl.n, cl.g / cl.n, cl.b / cl.n];
            merged = true;
            break;
          }
        }
        if (!merged) clusters.push({ r: bucket.r, g: bucket.g, b: bucket.b, n: bucket.n, avg: avg });
      }

      clusters.sort(function (p, q) { return q.n - p.n; });

      var totalN = 0;
      for (var ti = 0; ti < clusters.length; ti++) totalN += clusters[ti].n;
      if (!totalN) return [];

      var qualifying = clusters.filter(function (cl) { return (cl.n / totalN) >= 0.03; });
      if (!qualifying.length) qualifying = clusters.slice(0, 1);

      // The chosen clusters themselves — same top-N-by-size pick as
      // always. Kept as cluster objects (not yet rounded to [r,g,b])
      // so the near-white swap below can compare against and skip
      // these exact entries by reference.
      var chosen = qualifying.slice(0, maxColors);

      /*
        A small but genuinely vivid accent color — a scattered star
        print in blue, yellow or pink on an otherwise plain gray or
        heather garment, say — can lose out above purely on pixel
        count: the plain base fabric covers far more area, so the
        accent color never makes the top-N-by-size cut even though a
        human eye would call it "the color" of the piece. VIVID_CHROMA
        (distinctly saturated) combined with a small presence floor
        (0.5% of qualifying pixels — high enough to rule out single-
        pixel/compression noise, low enough to still catch a scattered
        print) finds those colors.

        Two separate guards keep this from reshuffling an outfit that
        wasn't the problem in the first place:
        - A `chosen` slot is only ever evicted if ITS OWN chroma is
          below EVICT_CHROMA_MAX — clearly gray/neutral, not just "the
          least colorful of the three". A moderately-colored but real,
          distinct hue (a navy, a muted teal) stays put even if
          something more saturated turns up elsewhere; only an
          actually-gray slot is up for replacement at all. (An earlier
          version of this used "whichever chosen color has the lowest
          chroma" as the only test, which could and did evict a real
          navy blue in favor of a second, near-duplicate shade of red
          already represented elsewhere in the same chosen set.)
        - A vivid candidate is only inserted if it's chromatically
          DISTINCT (colorDistance >= HUE_DISTINCT_MIN) from every color
          already in `chosen` — otherwise a photo with several close
          shades of the same hue (several pinks, say) would just swap
          one gray for a near-duplicate of a color already shown,
          rather than adding a genuinely different one.
        Together: a real accent color in a different hue than what's
        already selected always wins a slot over a redundant shade of
        gray; an outfit that's already showing its real, distinct
        saturated colors is completely unaffected either way.
      */
      var VIVID_CHROMA = 60;
      var VIVID_MIN_SHARE = 0.005;
      var EVICT_CHROMA_MAX = 25;
      var HUE_DISTINCT_MIN = 60;
      var vividClusters = clusters.filter(function (cl) {
        return chroma(cl.avg) >= VIVID_CHROMA && (cl.n / totalN) >= VIVID_MIN_SHARE;
      });
      for (var vi = 0; vi < vividClusters.length; vi++) {
        var vivid = vividClusters[vi];
        if (chosen.indexOf(vivid) !== -1) continue;
        var isDistinct = chosen.every(function (cl) {
          return colorDistance(vivid.avg, cl.avg) >= HUE_DISTINCT_MIN;
        });
        if (!isDistinct) continue;
        var weakestIdx = -1, weakestChroma = EVICT_CHROMA_MAX;
        for (var ci3 = 0; ci3 < chosen.length; ci3++) {
          var candidateChroma = chroma(chosen[ci3].avg);
          if (candidateChroma < weakestChroma) {
            weakestChroma = candidateChroma;
            weakestIdx = ci3;
          }
        }
        if (weakestIdx !== -1) chosen[weakestIdx] = vivid;
      }

      /*
        A chosen color that's white/near-white gets replaced with the
        next-most-prominent OTHER color anywhere in this photo's full
        cluster list — not just the top-N — that isn't itself
        near-white and isn't already one of the chosen colors. That's
        "the second color of the outfit" per color, found by walking
        the already-size-sorted `clusters` list in order. If nothing
        else in the whole photo qualifies (a genuinely all-white
        outfit with no second color at all), it's left as near-white
        here and darkened by the same pass as isLightGray below —
        never left invisible, never swapped to an arbitrary color.
      */
      for (var ri = 0; ri < chosen.length; ri++) {
        if (!isNearWhite(chosen[ri].avg)) continue;
        for (var ci2 = 0; ci2 < clusters.length; ci2++) {
          var candidate = clusters[ci2];
          if (chosen.indexOf(candidate) !== -1) continue;
          if (isNearWhite(candidate.avg)) continue;
          chosen[ri] = candidate;
          break;
        }
      }

      var result = [];
      for (var qi = 0; qi < chosen.length; qi++) {
        var cc = chosen[qi].avg;
        var rgb = [Math.round(cc[0]), Math.round(cc[1]), Math.round(cc[2])];
        if (isNearWhite(rgb) || isLightGray(rgb)) rgb = darkenGray(rgb);
        result.push(rgb);
      }
      return result;
    } catch (err) {
      return [];
    }
  }

  function readProgress(mediaEl) {
    var rect = mediaEl.getBoundingClientRect();
    var h = rect.height || 1;
    var scrolledUp = Math.max(0, -rect.top);
    return Math.max(0, Math.min(1, scrolledUp / (h * 0.55)));
  }

  function applyTextDrain(layerEl, progress) {
    if (!layerEl) return;
    var e = smoothstep(progress);
    var scale = 1 - e * 0.95;
    layerEl.style.transform = 'scale(' + scale.toFixed(4) + ')';
    layerEl.style.opacity = String(Math.max(0, 1 - progress * 1.4));
  }

  var VERT_SRC = [
    'attribute vec2 a_position;',
    'varying vec2 v_uv;',
    'void main() {',
    '  v_uv = a_position * 0.5 + 0.5;',
    '  gl_Position = vec4(a_position, 0.0, 1.0);',
    '}'
  ].join('\n');

  var FRAG_SRC = [
    'precision mediump float;',
    'varying vec2 v_uv;',
    'uniform sampler2D u_image;',
    'uniform vec2 u_canvasSize;',
    'uniform vec2 u_center;',
    'uniform float u_progress;',
    'void main() {',
    '  vec2 aspect = vec2(u_canvasSize.x / u_canvasSize.y, 1.0);',
    '  vec2 p = (v_uv - u_center) * aspect;',
    '  float d = length(p);',
    '  float e = u_progress * u_progress * (3.0 - 2.0 * u_progress);',
    '  float angle = u_progress * 12.0 * exp(-d * 4.0);',
    '  float s = sin(angle);',
    '  float c = cos(angle);',
    '  vec2 rotated = vec2(c * p.x - s * p.y, s * p.x + c * p.y);',
    '  float scale = mix(1.0, 0.015, e);',
    '  vec2 sourceP = (rotated / scale) / aspect + u_center;',
    '  if (sourceP.x >= 0.0 && sourceP.x <= 1.0 && sourceP.y >= 0.0 && sourceP.y <= 1.0) {',
    '    gl_FragColor = vec4(texture2D(u_image, sourceP).rgb, 1.0);',
    '  } else {',
    '    gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);',
    '  }',
    '}'
  ].join('\n');

  function compileShader(gl, type, src) {
    var sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      var info = gl.getShaderInfoLog(sh);
      gl.deleteShader(sh);
      throw new Error('shader compile failed: ' + info);
    }
    return sh;
  }

  /*
    Builds the whirlpool shader program once per banner (compile, link,
    the single full-screen quad, the one texture unit) — shared by both
    initPhoto (a single source image) and initComposite (a flattened
    multi-image scene), so the actual twirl-and-shrink math only exists
    in one place and both modes are guaranteed to render identically.
  */
  function createWhirlpoolProgram(gl) {
    var program = gl.createProgram();
    gl.attachShader(program, compileShader(gl, gl.VERTEX_SHADER, VERT_SRC));
    gl.attachShader(program, compileShader(gl, gl.FRAGMENT_SHADER, FRAG_SRC));
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error('program link failed: ' + gl.getProgramInfoLog(program));
    }
    gl.useProgram(program);

    var quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    var posLoc = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    var texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    return {
      program: program,
      quad: quad,
      posLoc: posLoc,
      texture: texture,
      u_image: gl.getUniformLocation(program, 'u_image'),
      u_canvasSize: gl.getUniformLocation(program, 'u_canvasSize'),
      u_center: gl.getUniformLocation(program, 'u_center'),
      u_progress: gl.getUniformLocation(program, 'u_progress')
    };
  }

  function uploadTextureSource(gl, prog, source) {
    gl.bindTexture(gl.TEXTURE_2D, prog.texture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
  }

  function resizeDrainCanvas(canvas, gl, dpr) {
    var rect = canvas.getBoundingClientRect();
    var w = Math.max(1, Math.round(rect.width * dpr));
    var h = Math.max(1, Math.round(rect.height * dpr));
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    gl.viewport(0, 0, canvas.width, canvas.height);
  }

  function renderWhirlpoolFrame(gl, canvas, prog, progress) {
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    gl.useProgram(prog.program);
    gl.bindBuffer(gl.ARRAY_BUFFER, prog.quad);
    gl.enableVertexAttribArray(prog.posLoc);
    gl.vertexAttribPointer(prog.posLoc, 2, gl.FLOAT, false, 0, 0);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, prog.texture);
    gl.uniform1i(prog.u_image, 0);
    gl.uniform2f(prog.u_canvasSize, canvas.width, canvas.height);
    gl.uniform2f(prog.u_center, 0.5, 0.5);
    gl.uniform1f(prog.u_progress, progress);

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  /*
    Photo mode (image banners): identical whirlpool/drain mechanics to
    the original Sets build (twirl-then-shrink WebGL shader pulling the
    photo toward the stars' shared center, hard binary alpha cutoff so
    the stars behind are revealed through the gaps, no pinning). Color
    priority per star, highest first: this banner's own manual override
    (collection metafield, then section setting — see applyColor below),
    else the dominant garment colors auto-detected from its own photo,
    else a neutral gray if detection can't find a confident answer.
    Never falls back to black — black is reserved for genuinely
    text-only banners (see initText).
  */
  function initPhoto(opts) {
    var mediaEl = opts.mediaEl, picEl = opts.picEl, imgEl = opts.imgEl,
      layerEl = opts.layerEl, canvas = opts.canvas, starsEl = opts.starsEl,
      starPaths = opts.starPaths, wordmarkPaths = opts.wordmarkPaths,
      overrideColor = opts.overrideColor;
    if (!mediaEl || !picEl || !imgEl || !canvas || !starsEl) return;

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /*
      wordmarkPaths (each collection's word under the stars — the
      Sets-only hardcoded "SETS" markup in sections/new-image-banner.liquid,
      or the generic snippets/drain-banner-wordmark.liquid used by every
      other collection running this effect) are colored from the exact
      same array as the stars, computed once here rather than detected
      or chosen separately — never out of sync with whatever the stars
      end up showing, and empty/absent on any banner with no wordmark
      so this is a no-op there.
    */
    function applyColor() {
      var overrideColors = parseColorList(overrideColor);
      var colors = overrideColors || detectGarmentColors(imgEl, 3);
      setStarColors(starPaths, colors);
      if (wordmarkPaths && wordmarkPaths.length) setStarColors(wordmarkPaths, colors);
      markDrainReady(mediaEl);
    }

    /*
      Waits for the image to actually finish loading before sampling it
      for color, exactly like the WebGL activate() path below does. An
      earlier version of this file skipped that wait here specifically —
      calling applyColor() immediately — which meant that on a
      reduced-motion visit, or whenever WebGL context creation failed
      and this ran from the catch block, color detection could run
      against a lazy-loaded <img> that had zero natural width/height
      (its fetch hadn't finished), always failing and silently
      collapsing to the (then near-black) fallback. That race is why
      photo banners could render black stars even though detection
      itself worked fine once an image was actually loaded.
    */
    function applyColorWhenLoaded() {
      if (imgEl.complete && imgEl.naturalWidth > 0) {
        applyColor();
      } else {
        imgEl.addEventListener('load', applyColor, { once: true });
      }
    }

    function runFallback() {
      applyColorWhenLoaded();
      function frame() {
        var progress = readProgress(mediaEl);
        picEl.style.opacity = String(1 - progress);
        applyTextDrain(layerEl, progress);
      }
      window.addEventListener('scroll', frame, { passive: true });
      window.addEventListener('resize', frame, { passive: true });
      frame();
    }

    if (reducedMotion) {
      runFallback();
      return;
    }

    try {
      var gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false }) ||
        canvas.getContext('experimental-webgl', { alpha: true, premultipliedAlpha: false });
      if (!gl) throw new Error('no webgl context');

      var prog = createWhirlpoolProgram(gl);
      var textureReady = false;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);

      function render() {
        var progress = readProgress(mediaEl);
        resizeDrainCanvas(canvas, gl, dpr);
        renderWhirlpoolFrame(gl, canvas, prog, progress);
        applyTextDrain(layerEl, progress);
      }

      function onScrollOrResize() {
        if (!textureReady) return;
        render();
      }

      function activate() {
        applyColor();
        uploadTextureSource(gl, prog, imgEl);
        textureReady = true;
        picEl.style.opacity = '0';
        resizeDrainCanvas(canvas, gl, dpr);
        window.addEventListener('scroll', onScrollOrResize, { passive: true });
        window.addEventListener('resize', onScrollOrResize, { passive: true });
        render();
        // One more measure+render next frame: activate() can run inside
        // an <img> 'load' handler before the browser has committed a
        // layout pass for this element, which can read a stale/default
        // canvas size (0 or the browser's intrinsic 300x150) the very
        // first time — self-corrects on the next scroll regardless, but
        // this avoids a single soft/blurry frame before that happens.
        window.requestAnimationFrame(render);
      }

      if (imgEl.complete && imgEl.naturalWidth > 0) {
        activate();
      } else {
        imgEl.addEventListener('load', activate, { once: true });
      }
    } catch (err) {
      runFallback();
    }
  }

  /*
    Composite mode (position-image-banner's "Triple studio images":
    one main center photo plus two separately-positioned side overlay
    photos — e.g. the Men's T-Shirts banner). There is no single bitmap
    here the way there is for initPhoto, so this flattens the CURRENT
    on-screen layout — main image plus whichever side images are
    present, at their real rendered positions and sizes — onto one
    offscreen 2D canvas, then feeds THAT as the texture to the exact
    same whirlpool shader initPhoto uses. The whole banner — all three
    images together — twirls and drains as one scene into the stars,
    identically to a single-image banner, rather than the simpler
    scale+rotate CSS transform a plain text banner uses.

    The flattening only needs to happen once at activation, and again if
    the window resizes (a real layout/breakpoint change moves the side
    images) — not on every scroll frame, since scrolling never changes
    the images' positions relative to each other, only the whole
    banner's position in the viewport.
  */
  function initComposite(opts) {
    var mediaEl = opts.mediaEl, canvas = opts.canvas, starsEl = opts.starsEl,
      starPaths = opts.starPaths, contentEl = opts.contentEl,
      sectionEl = opts.sectionEl, mainImgEl = opts.mainImgEl,
      mainImgElMobile = opts.mainImgElMobile, leftImgEl = opts.leftImgEl,
      rightImgEl = opts.rightImgEl, overrideColor = opts.overrideColor,
      wordmarkPaths = opts.wordmarkPaths;
    if (!mediaEl || !canvas || !starsEl || !contentEl || !mainImgEl) return;

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /*
      Whichever main image is actually laid out right now (desktop vs.
      mobile breakpoint swap — see .studio-stack__main--desktop/mobile).
      Used for both color detection and compositing, and re-evaluated
      rather than cached once: an earlier version hardcoded color
      detection to always read the desktop image, which on a mobile
      viewport (where that image sits display: none and, being
      loading="lazy", may never actually fetch) meant naturalWidth was
      stuck at 0, detection always failed, and stars fell back to
      neutral gray on mobile even though the exact same banner worked
      correctly on desktop.
    */
    function activeMainImg() {
      if (mainImgEl && mainImgEl.getBoundingClientRect().width > 0) return mainImgEl;
      if (mainImgElMobile && mainImgElMobile.getBoundingClientRect().width > 0) return mainImgElMobile;
      return mainImgEl;
    }

    function applyColor() {
      var overrideColors = parseColorList(overrideColor);
      var colors = overrideColors || detectGarmentColors(activeMainImg(), 3);
      setStarColors(starPaths, colors);
      if (wordmarkPaths && wordmarkPaths.length) setStarColors(wordmarkPaths, colors);
      markDrainReady(mediaEl);
    }

    function applyColorWhenLoaded() {
      var colorSrc = activeMainImg();
      if (colorSrc.complete && colorSrc.naturalWidth > 0) {
        applyColor();
      } else {
        colorSrc.addEventListener('load', applyColor, { once: true });
      }
    }

    function runFallback() {
      applyColorWhenLoaded();
      function frame() {
        var progress = readProgress(mediaEl);
        applyTextDrain(contentEl, progress);
      }
      window.addEventListener('scroll', frame, { passive: true });
      window.addEventListener('resize', frame, { passive: true });
      frame();
    }

    if (reducedMotion) {
      runFallback();
      return;
    }

    try {
      var gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false }) ||
        canvas.getContext('experimental-webgl', { alpha: true, premultipliedAlpha: false });
      if (!gl) throw new Error('no webgl context');

      var prog = createWhirlpoolProgram(gl);
      var textureReady = false;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);

      var compositeCanvas = document.createElement('canvas');
      var cctx = compositeCanvas.getContext('2d');

      function drawAt(img, rect, originRect) {
        if (!img || rect.width <= 0 || rect.height <= 0) return;
        cctx.drawImage(img, rect.left - originRect.left, rect.top - originRect.top, rect.width, rect.height);
      }

      // Rasterizes the current layout — background scheme color first
      // (so the padding/margin area around the images matches the
      // banner's own color scheme instead of coming out blank), then
      // the main image, then the two side overlays on top, same visual
      // stacking order as the live DOM.
      function updateComposite() {
        var rect = mediaEl.getBoundingClientRect();
        var w = Math.max(1, Math.round(rect.width));
        var h = Math.max(1, Math.round(rect.height));
        compositeCanvas.width = w;
        compositeCanvas.height = h;
        cctx.clearRect(0, 0, w, h);

        if (sectionEl) {
          var bg = getComputedStyle(sectionEl).backgroundColor;
          if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
            cctx.fillStyle = bg;
            cctx.fillRect(0, 0, w, h);
          }
        }

        var main = activeMainImg();
        if (main) drawAt(main, main.getBoundingClientRect(), rect);
        if (leftImgEl) drawAt(leftImgEl, leftImgEl.getBoundingClientRect(), rect);
        if (rightImgEl) drawAt(rightImgEl, rightImgEl.getBoundingClientRect(), rect);
      }

      function uploadComposite() {
        updateComposite();
        uploadTextureSource(gl, prog, compositeCanvas);
        textureReady = true;
      }

      function render() {
        var progress = readProgress(mediaEl);
        resizeDrainCanvas(canvas, gl, dpr);
        renderWhirlpoolFrame(gl, canvas, prog, progress);
      }

      function onScroll() {
        if (!textureReady) return;
        render();
      }

      // A real window resize can move/resize the side overlays (they're
      // positioned per breakpoint), so the flattened texture is rebuilt
      // — not just the canvas's own pixel size, the way initPhoto's
      // resize-on-scroll-or-resize is enough for a single static image.
      function onLayoutResize() {
        if (!textureReady) return;
        uploadComposite();
        render();
      }

      function activate() {
        applyColor();
        uploadComposite();
        contentEl.style.opacity = '0';
        resizeDrainCanvas(canvas, gl, dpr);
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onLayoutResize, { passive: true });
        render();
        // One more measure+recomposite next frame: activate() can run
        // inside an <img> 'load' handler before the browser has
        // committed a layout pass, which can read stale/default
        // rects for the canvas and the images being flattened — self-
        // corrects on the next scroll regardless, but this avoids a
        // single soft/blurry or mis-laid-out frame before that happens.
        window.requestAnimationFrame(function () {
          uploadComposite();
          render();
        });
      }

      /*
        Only wait for the main image that's actually going to be drawn —
        not both the desktop and mobile variants. When a collection has
        a mobile-specific main image set, the OTHER one sits in the DOM
        with display: none at the current breakpoint; being hidden and
        loading="lazy" together mean browsers can leave it in a
        perpetual not-yet-loaded state (no load/error event ever fires
        for an image the browser has decided is never going to be
        shown), which silently hung activation forever waiting for an
        image nothing actually needs — the whole banner stayed at its
        pre-JS defaults (plain SVG black stars, full opacity) with no
        error, since this was simply stuck waiting, not throwing.
      */
      var toWatch = [activeMainImg(), leftImgEl, rightImgEl].filter(Boolean);
      var remaining = toWatch.length;
      function onOneLoaded() {
        remaining--;
        if (remaining <= 0) activate();
      }
      if (!toWatch.length) {
        activate();
      } else {
        toWatch.forEach(function (im) {
          if (im.complete && im.naturalWidth > 0) {
            onOneLoaded();
          } else {
            im.addEventListener('load', onOneLoaded, { once: true });
            im.addEventListener('error', onOneLoaded, { once: true });
          }
        });
      }
    } catch (err) {
      runFallback();
    }
  }

  /*
    Text mode (no-photo collection banners, e.g. a plain collection
    title on a background color): there is no single bitmap to feed a
    WebGL texture, so the whole content block spirals inward and
    shrinks via a CSS transform instead of a per-pixel shader —
    rotating as it shrinks for the same "pulled down the drain" read.
    Stars are always solid black here (the genuinely text-only case —
    see initComposite above for the photo-but-not-one-rectangle case,
    which gets colored stars and the real shader instead of this).
  */
  function initText(opts) {
    var mediaEl = opts.mediaEl, contentEl = opts.contentEl,
      starsEl = opts.starsEl, starPaths = opts.starPaths,
      backgroundEl = opts.backgroundEl;
    if (!mediaEl || !contentEl || !starsEl) return;

    setStarColors(starPaths, [[0, 0, 0]]);

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function frame() {
      var progress = readProgress(mediaEl);
      var e = smoothstep(progress);
      if (backgroundEl) backgroundEl.style.opacity = String(Math.max(0, 1 - progress * 1.2));
      if (reducedMotion) {
        contentEl.style.transform = 'scale(' + (1 - e * 0.95).toFixed(4) + ')';
      } else {
        var scale = 1 - e * 0.95;
        var rotate = e * 420; // degrees — gives the shrink a swirling, "down the drain" read
        contentEl.style.transform = 'scale(' + scale.toFixed(4) + ') rotate(' + rotate.toFixed(2) + 'deg)';
      }
      contentEl.style.opacity = String(Math.max(0, 1 - progress * 1.4));
    }

    window.addEventListener('scroll', frame, { passive: true });
    window.addEventListener('resize', frame, { passive: true });
    frame();
  }

  window.StarSniffDrainBanner = {
    initPhoto: initPhoto,
    initComposite: initComposite,
    initText: initText,
    detectGarmentColors: detectGarmentColors,
    hexToRgb: hexToRgb,
    rgbToCss: rgbToCss
  };

  /*
    Self-bootstrapping scan, so no section needs its own inline <script>
    to call into this file (which would risk running before this
    deferred script has executed). Any element carrying
    data-drain-banner picks up mode/color from its own data attributes
    and finds its children by the shared .drain-banner__* class names;
    a section only has to emit markup, never JS. Guarded per-element so
    re-running this scan (e.g. if two banners on one page both load this
    same script tag) never double-initializes the same banner.
  */
  function bootstrap() {
    var roots = document.querySelectorAll('[data-drain-banner]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      if (root.hasAttribute('data-drain-banner-initialized')) continue;
      root.setAttribute('data-drain-banner-initialized', '');

      var mode = root.getAttribute('data-drain-mode');
      var starsEl = root.querySelector('.drain-banner__stars');
      var starPaths = starsEl ? Array.prototype.slice.call(starsEl.querySelectorAll('.drain-banner__star')) : [];

      if (mode === 'photo') {
        initPhoto({
          mediaEl: root,
          picEl: root.querySelector('.drain-banner__pic'),
          imgEl: root.querySelector('.drain-banner__img'),
          layerEl: root.querySelector('.drain-banner__text-layer'),
          canvas: root.querySelector('.drain-banner__canvas'),
          starsEl: starsEl,
          starPaths: starPaths,
          wordmarkPaths: Array.prototype.slice.call(root.querySelectorAll('.drain-banner__setstext-letter')),
          overrideColor: root.getAttribute('data-drain-color') || null
        });
      } else if (mode === 'text') {
        initText({
          mediaEl: root,
          contentEl: root.querySelector('.drain-banner__content'),
          starsEl: starsEl,
          starPaths: starPaths,
          backgroundEl: root.querySelector('.drain-banner__white-bg')
        });
      } else if (mode === 'composite') {
        var mainDesktopWrap = root.querySelector('.studio-stack__main--desktop');
        var mainMobileWrap = root.querySelector('.studio-stack__main--mobile');
        initComposite({
          mediaEl: root,
          canvas: root.querySelector('.drain-banner__canvas'),
          starsEl: starsEl,
          starPaths: starPaths,
          contentEl: root.querySelector('.drain-banner__content'),
          sectionEl: root.querySelector('.studio-stack-section'),
          mainImgEl: mainDesktopWrap ? mainDesktopWrap.querySelector('img') : null,
          mainImgElMobile: mainMobileWrap ? mainMobileWrap.querySelector('img') : null,
          leftImgEl: root.querySelector('.studio-stack__side--left img'),
          rightImgEl: root.querySelector('.studio-stack__side--right img'),
          wordmarkPaths: Array.prototype.slice.call(root.querySelectorAll('.drain-banner__setstext-letter')),
          overrideColor: root.getAttribute('data-drain-color') || null
        });
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
